import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ticketVerifyUrl } from '@buspro/shared/common';
import { type FutureBookingReceiptDto, FutureBookingsApi } from '@buspro/web/apis';
import { DEFAULT_COMPANY_LOGO, WEB_CONFIG, WebAppEnvironment } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { QRCodeModule } from 'angularx-qrcode';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom } from 'rxjs';
import { PrintService } from './print.service';

@Component({
  selector: 'tenant-future-booking-receipt-print-sheet',
  standalone: true,
  imports: [CommonModule, MomentModule, QRCodeModule],
  template: `
    <div class="invoice bg-gray-100">
      <div class="print-button-container">
        <button type="button" class="btn btn-primary btn-lg shadow-lg" (click)="printService.onDataReady()">
          <span class="fe fe-printer"></span>
          {{ cancellationSlip() ? 'Print cancellation slip' : 'Print receipt' }}
        </button>
      </div>

      @if (receiptQuery.isFetched() && receiptQuery.data(); as receipt) {
        <div id="fb-receipt" class="card bg-white w-full p-4 mx-auto space-y-6" [ngStyle]="{ width: '80mm' }">
          @if (cancellationSlip()) {
            <div class="rounded bg-error/10 text-error text-center text-sm font-semibold py-2">Cancelled reservation</div>
          } @else {
            <div class="text-center text-xs font-semibold uppercase tracking-wide text-primary">Future reservation</div>
          }

          <div class="flex justify-between items-center gap-2">
            <div>
              <div class="font-bold text-primary-focus text-lg">{{ receipt.companyName }}</div>
              <div class="text-gray-700 text-sm">{{ cancellationSlip() ? 'Cancellation slip' : 'Reservation receipt' }}</div>
            </div>
            <div class="w-20 shrink-0">
              <img [src]="receipt.companyLogoUrl || companyLogoFallback" alt="company logo" class="max-h-16 object-contain" />
            </div>
          </div>

          <div class="divide-y space-y-3 text-sm">
            <div class="space-y-2 pb-2">
              <div class="flex justify-between gap-2" *ngIf="receipt.mainRouteName">
                <span class="text-xs text-gray-500">Travel Route</span>
                <span class="text-right text-xs font-semibold">{{ receipt.mainRouteName }}</span>
              </div>
              <!-- The leg the fare is for. The route above is the bus's whole
                   run; the total below buys only this part of it. -->
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Your Journey</span>
                <span class="text-right text-xs font-semibold">{{ receipt.journeyLeg || receipt.subRouteName }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Travel date</span>
                <span class="text-right text-xs font-semibold">{{ receipt.travelDate | amDateFormat : 'll' }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Departure</span>
                <span class="text-right text-xs font-semibold">{{ receipt.departureDateTime | amDateFormat : 'lll' }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Bus type</span>
                <span class="text-right text-xs font-semibold">{{ receipt.busTypeName }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Seat class</span>
                <span class="text-right text-xs font-semibold">{{ receipt.seatClass }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Seat</span>
                <span class="text-right text-xs font-semibold">{{ receipt.seatDisplay }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Status</span>
                <span class="text-right text-xs font-semibold">{{ receipt.status }}</span>
              </div>
            </div>
            <div class="space-y-2 pt-2">
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Customer</span>
                <span class="text-right text-xs font-semibold">{{ receipt.customerName }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Phone</span>
                <span class="text-right text-xs font-semibold">{{ receipt.customerPhoneNumber }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-xs text-gray-500">Served by</span>
                <span class="text-right text-xs font-semibold">{{ receipt.servedBy }}</span>
              </div>
            </div>
            @if (receipt.quotedFare !== null && receipt.quotedFare !== undefined && receipt.quotedFareCurrency) {
              <div class="flex justify-between gap-2 pt-2 font-bold">
                <span>Quoted fare</span>
                <span class="text-success">
                  {{ receipt.quotedFare | currency : receipt.quotedFareCurrency : 'symbol' : '1.0-0' }}
                </span>
              </div>
            }
          </div>

          <div class="flex flex-col items-center mt-2">
            <h4 class="text-xs font-semibold mb-1">Scan to verify</h4>
            <qrcode [qrdata]="verificationUrl(receipt)" [width]="140" [errorCorrectionLevel]="'M'"></qrcode>
            <div class="text-xs text-gray-500 mt-1">Ticket # {{ receipt.ticketNumber }}</div>
          </div>

          <div class="border-t pt-2 mt-2 text-xxs text-gray-600 space-y-1">
            <p class="text-center font-semibold">Terms &amp; conditions</p>
            <ul class="list-decimal pl-4 space-y-0.5">
              <li>Valid only for the company, route, and travel date shown.</li>
              <li>Arrive at least 30 minutes before departure.</li>
              <li>Refund rules follow company policy and captured payment (if any).</li>
            </ul>
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    .invoice {
      page-break-after: always;
    }
    .invoice:last-child {
      page-break-after: avoid;
    }
    .print-button-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 1000;
    }
    .btn-lg {
      padding: 0.75rem 1.5rem;
      font-size: 1.25rem;
    }
    .text-xxs {
      font-size: 0.65rem;
      line-height: 1rem;
    }
    @media print {
      .print-button-container {
        display: none;
      }
      #fb-receipt {
        width: 88mm;
      }
      @page {
        size: 80mm auto;
        margin: 0;
      }
    }
  `,
})
export class FutureBookingReceiptPrintSheetComponent {
  printService = inject(PrintService);
  private readonly api = inject(FutureBookingsApi);
  private readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  protected readonly companyLogoFallback = DEFAULT_COMPANY_LOGO;

  readonly documentKeys = signal<string | null>(null);

  readonly bookingId = computed(() => {
    const raw = this.documentKeys();
    if (!raw) {
      return null;
    }
    return raw.split('|')[0] ?? null;
  });

  readonly cancellationSlip = computed(() => {
    const raw = this.documentKeys();
    if (!raw) {
      return false;
    }
    return raw.split('|')[1] === 'cancellation';
  });

  receiptQuery = injectQuery(() => ({
    queryKey: ['future-booking-receipt-print', this.bookingId(), this.cancellationSlip()],
    queryFn: () => lastValueFrom(this.api.futureBookingsControllerGetReceipt({ id: this.bookingId()! })),
    enabled: !!this.bookingId(),
  }));

  constructor(route: ActivatedRoute) {
    this.documentKeys.set(route.snapshot.params['documentId'] ?? null);
  }

  verificationUrl(receipt: FutureBookingReceiptDto): string {
    const baseUrl = this.environment.publicWebsiteUrl || 'https://buspro.biz';
    // The QR names the company: a short ticket number is unique only within one,
    // so a scan has to say which operator issued it. The public page still
    // accepts the legacy `?ticketNumber=` link for every slip already printed.
    return ticketVerifyUrl(baseUrl, receipt.companyId, receipt.ticketNumber);
  }
}
